import { FilterPills } from "@/components/molecules/filter-pills";
import { SearchableSelect } from "@/components/molecules/searchable-select";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useAdminTenants,
  useToolErrorList,
  useToolErrors,
  type ToolErrorFilters,
  type ToolErrorKind,
  type ToolErrorPhase,
} from "@/features/admin";
import { useMemo, useState } from "react";
import { StatCard } from "../analytics/components/stat-card";
import { AdminPageLayout } from "../layout";
import { ErrorList } from "./components/error-list";
import { ToolTable } from "./components/tool-table";
import { errorTone, pct } from "./format";

const WINDOWS = [
  { hours: 24, label: "24 dernières heures" },
  { hours: 24 * 7, label: "7 derniers jours" },
  { hours: 24 * 30, label: "30 derniers jours" },
];

const PAGE_SIZE = 50;

type All = "all";

const KIND_OPTIONS: { value: ToolErrorKind | All; label: string }[] = [
  { value: "all", label: "Toutes" },
  { value: "incident", label: "Incidents" },
  { value: "instruction", label: "Instructions à l'agent" },
];

const PHASE_OPTIONS: { value: ToolErrorPhase | All; label: string }[] = [
  { value: "all", label: "Toutes phases" },
  { value: "call", label: "Pendant l'appel" },
  { value: "wrapup", label: "Wrap-up" },
];

/**
 * Tool calls that came back in error during conversations, across every tenant.
 *
 * Two very different things share `status: "error"`: an **incident** (an integration threw,
 * a timeout — something to fix) and an **instruction** the tool addressed to the agent ("ask
 * for the email first" — the tool doing its job). The kind filter separates them, and
 * "rattrapé" says the agent called the same tool successfully later in the session.
 */
export function AdminToolErrorsPage() {
  const [hours, setHours] = useState(24 * 7);
  const [client, setClient] = useState<string>("");
  const [tool, setTool] = useState<string | undefined>();
  const [kind, setKind] = useState<ToolErrorKind | All>("all");
  const [phase, setPhase] = useState<ToolErrorPhase | All>("all");
  const [offset, setOffset] = useState(0);

  const { data: tenants = [], isLoading: tenantsLoading } =
    useAdminTenants(30);

  const base: ToolErrorFilters = useMemo(
    () => ({
      client: client || undefined,
      kind: kind === "all" ? undefined : kind,
      phase: phase === "all" ? undefined : phase,
    }),
    [client, kind, phase],
  );
  const listFilters = useMemo(() => ({ ...base, tool }), [base, tool]);

  const { data, isLoading } = useToolErrors(hours, base);
  const { data: page, isLoading: listLoading } = useToolErrorList(
    hours,
    listFilters,
    { limit: PAGE_SIZE, offset },
  );

  // Any filter change sends the list back to its first page.
  const reset =
    <T,>(set: (v: T) => void) =>
    (v: T) => {
      set(v);
      setOffset(0);
    };

  const totals = data?.totals;

  return (
    <AdminPageLayout
      title="Outils en erreur"
      description={
        totals
          ? `${totals.errors} erreur(s) sur ${totals.calls} appel(s) d'outil sur la période`
          : "Appels d'outils revenus en erreur pendant les conversations"
      }
      actions={[
        <Select
          key="window"
          value={String(hours)}
          onValueChange={(v) => reset(setHours)(Number(v))}
        >
          <SelectTrigger className="w-[220px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {WINDOWS.map((w) => (
              <SelectItem key={w.hours} value={String(w.hours)}>
                {w.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>,
      ]}
    >
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-[260px]">
          <SearchableSelect
            variant="select"
            items={tenants.map((t) => ({ id: t.id, label: t.name }))}
            value={client}
            onChange={(v) => reset(setClient)((Array.isArray(v) ? v[0] : v) || "")}
            isLoading={tenantsLoading}
            allowNone
            noneOption={{ id: "__all", label: "Tous les clients" }}
            placeholder="Tous les clients"
            searchPlaceholder="Rechercher…"
            popoverWidth="w-[300px]"
          />
        </div>
        <FilterPills
          label="Type d'erreur"
          value={kind}
          onChange={reset(setKind)}
          options={KIND_OPTIONS}
        />
        <FilterPills
          label="Phase"
          value={phase}
          onChange={reset(setPhase)}
          options={PHASE_OPTIONS}
        />
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          title="Erreurs"
          value={totals ? totals.errors.toLocaleString("fr-FR") : "—"}
          hint={
            totals
              ? `${totals.calls.toLocaleString("fr-FR")} appel(s) d'outil`
              : undefined
          }
          isLoading={isLoading}
        />
        <StatCard
          title="Taux d'erreur"
          value={totals ? pct(totals.error_rate) : "—"}
          isLoading={isLoading}
          tone={totals ? errorTone(totals.error_rate) : "default"}
        />
        <StatCard
          title="Incidents"
          value={totals ? totals.incidents.toLocaleString("fr-FR") : "—"}
          hint="Intégration en échec ou timeout"
          isLoading={isLoading}
          tone={totals && totals.incidents ? "bad" : "default"}
        />
        <StatCard
          title="Sessions touchées"
          value={totals ? totals.sessions.toLocaleString("fr-FR") : "—"}
          hint={
            totals
              ? `${totals.clients} client(s), ${totals.recovered} erreur(s) rattrapée(s)`
              : undefined
          }
          isLoading={isLoading}
        />
      </div>

      <ToolTable
        rows={data?.tools || []}
        isLoading={isLoading}
        selected={tool}
        onSelect={reset(setTool)}
      />

      {tool ? (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Filtré sur</span>
          <span className="font-mono font-medium">{tool}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => reset(setTool)(undefined)}
          >
            Tout afficher
          </Button>
        </div>
      ) : null}

      <ErrorList
        rows={page?.errors || []}
        total={page?.total || 0}
        offset={offset}
        pageSize={PAGE_SIZE}
        isLoading={listLoading}
        onPage={setOffset}
        tool={tool}
      />
    </AdminPageLayout>
  );
}
